import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link, useNavigate } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import {
   Grid,
   TextField,
   Box,
   Typography,
   FormControl,
   InputLabel,
   Select,
   MenuItem,
   Button,
   Paper,
   Divider,
} from '@mui/material';
import React, { useState } from 'react';
import axios from 'axios';
import { API_URL } from '../../Config';
import { toast } from 'react-toastify';
import { getAuthToken } from '../../Redux/Service/FetchBaseQuery';
import StyledDashboardLayout from '../style';
import * as Yup from 'yup';

const AddTechstack = () => {
   const navigate = useNavigate();
   const [loading, setLoading] = useState(false);

   // Technology types for the dropdown
   const technologyTypes = [
      { value: 'Frontend', label: 'Frontend' },
      { value: 'Backend', label: 'Backend' },
      { value: 'Database', label: 'Database' },
      { value: 'DevOps', label: 'DevOps' },
      { value: 'Mobile', label: 'Mobile' },
      { value: 'CMS', label: 'CMS' },
      { value: 'UI/UX', label: 'UI/UX' },
      { value: 'SEO', label: 'SEO' },
   ];

   // Initial form values
   const initialValues = {
      type: '',
      technology: '',
   };

   // Validation schema
   const validationSchema = Yup.object().shape({
      type: Yup.string().required('Technology type is required'),
      technology: Yup.string()
         .trim()
         .required('Technology name is required')
         .matches(/^[^0-9]*$/, 'Numbers are not allowed')
         .min(2, 'Technology name must be at least 2 characters')
         .max(100, 'Technology name must not exceed 100 characters'),
   });

   // Handle form submission
   const handleSubmit = async (values, { setSubmitting, resetForm }) => {
      try {
         setSubmitting(true);
         setLoading(true);
         const token = getAuthToken();
         Object.entries(values).forEach(([key, val]) =>
            typeof val === 'string' ? (values[key] = val.trim()) : val,
         );

         const response = await axios.post(`${API_URL}technology-stack/create`, values, {
            headers: {
               Authorization: `Bearer ${token}`,
               'Content-Type': 'application/json',
            },
         });

         console.log('Tech Stack added successfully:', response);
         toast.success(response?.data?.message || 'Tech Stack created successfully!');

         // Reset form and navigate back
         resetForm();
         navigate('/tech-stack/techstacklist');
      } catch (error) {
         console.error('Error creating tech stack:', error);
         toast.error(error?.response?.data?.message || 'Failed to add tech stack');
      } finally {
         setLoading(false);
         setSubmitting(false);
      }
   };

   return (
      <StyledDashboardLayout>
         <div className="content-wrapper">
            <div className="content-header">
               <div className="container-fluid">
                  <div className="d-flex justify-content-between mb-2">
                     <div className="pageTitle">
                        <h1 className="m-0">Add Technology Stack</h1>
                     </div>
                     <div className="btnWrap">
                        <Link to="/tech-stack/techstacklist" className="btn addBtn">
                           <ArrowBackIcon /> Back
                        </Link>
                     </div>
                  </div>
               </div>
            </div>

            <div className="card customCard p-3 m-3">
               <Paper elevation={0} sx={{ p: 3 }}>
                  <Typography
                     variant="h5"
                     component="h2"
                     gutterBottom
                     sx={{
                        fontWeight: 600,
                        color: '#333',
                        mb: 3,
                        display: 'flex',
                        alignItems: 'center',
                     }}
                  >
                     Technology Stack Information
                  </Typography>

                  <Divider sx={{ mb: 3 }} />

                  <Formik
                     initialValues={initialValues}
                     validationSchema={validationSchema}
                     onSubmit={handleSubmit}
                  >
                     {({
                        values,
                        setFieldValue,
                        touched,
                        errors,
                        isSubmitting,
                        handleBlur,
                        handleChange,
                     }) => (
                        <Form>
                           <Box className="card-body" sx={{ padding: '0' }}>
                              <Grid container spacing={3}>
                                 {/* Technology Type */}
                                 <Grid item xs={12} md={6}>
                                    <FormControl
                                       fullWidth
                                       error={touched.type && Boolean(errors.type)}
                                    >
                                       <InputLabel id="technology-type-label">
                                          Technology Type *
                                       </InputLabel>
                                       <Field
                                          name="type"
                                          as={Select}
                                          labelId="technology-type-label"
                                          label="Technology Type *"
                                          value={values.type}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          sx={{
                                             '& .MuiOutlinedInput-root': {
                                                '&:hover fieldset': {
                                                   borderColor: '#3F30FF',
                                                },
                                                '&.Mui-focused fieldset': {
                                                   borderColor: '#3F30FF',
                                                },
                                             },
                                          }}
                                       >
                                          <MenuItem value="" disabled>
                                             Select Technology Type
                                          </MenuItem>
                                          {technologyTypes.map((type) => (
                                             <MenuItem key={type.value} value={type.value}>
                                                {type.label}
                                             </MenuItem>
                                          ))}
                                       </Field>
                                       <ErrorMessage
                                          name="type"
                                          component="div"
                                          style={{
                                             color: '#d32f2f',
                                             fontSize: '0.75rem',
                                             marginTop: '3px',
                                             marginLeft: '14px',
                                          }}
                                       />
                                    </FormControl>
                                 </Grid>

                                 {/* Technology Name */}
                                 <Grid item xs={12} md={6}>
                                    <Field
                                       name="technology"
                                       as={TextField}
                                       fullWidth
                                       label="Technology Name *"
                                       variant="outlined"
                                       value={values.technology}
                                       onChange={handleChange}
                                       onBlur={handleBlur}
                                       error={touched.technology && Boolean(errors.technology)}
                                       helperText={touched.technology && errors.technology}
                                       placeholder="e.g., React, Node.js, MySQL"
                                       sx={{
                                          '& .MuiOutlinedInput-root': {
                                             '&:hover fieldset': {
                                                borderColor: '#3F30FF',
                                             },
                                             '&.Mui-focused fieldset': {
                                                borderColor: '#3F30FF',
                                             },
                                          },
                                          '& .MuiInputLabel-root': {
                                             '&.Mui-focused': {
                                                color: '#3F30FF',
                                             },
                                          },
                                       }}
                                    />
                                 </Grid>

                                 {/* Submit Button */}
                                 <Grid item xs={12}>
                                    <Box
                                       sx={{
                                          display: 'flex',
                                          gap: 2,
                                          justifyContent: 'flex-end',
                                          mt: 2,
                                       }}
                                    >
                                       <Button
                                          type="button"
                                          variant="outlined"
                                          onClick={() => navigate('/tech-stack/techstacklist')}
                                          sx={{
                                             borderColor: '#6c757d',
                                             color: '#6c757d',
                                             '&:hover': {
                                                borderColor: '#545b62',
                                                backgroundColor: '#f8f9fa',
                                             },
                                          }}
                                       >
                                          Cancel
                                       </Button>
                                       <Button
                                          type="submit"
                                          variant="contained"
                                          disabled={isSubmitting || loading}
                                          className="commonBtn"
                                          sx={{
                                             backgroundColor: '#3F30FF',
                                             '&:hover': {
                                                backgroundColor: '#2f23cc',
                                             },
                                             minWidth: '120px',
                                          }}
                                       >
                                          {isSubmitting || loading
                                             ? 'Saving...'
                                             : 'Save Tech Stack'}
                                       </Button>
                                    </Box>
                                 </Grid>
                              </Grid>
                           </Box>
                        </Form>
                     )}
                  </Formik>
               </Paper>
            </div>
         </div>
      </StyledDashboardLayout>
   );
};

export default AddTechstack;
